import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../config";
import {
  getReviewClassifications,
  getReviewDetail,
  reviewClassification,
  batchReviewClassifications,
  getReviewActivity,
} from "../requests/review.requests";
import { ReviewFilters, SingleReviewPayload, BatchReviewPayload } from "../../types/review";

const DEFAULT_PAGE_SIZE = 100;

interface UseReviewClassificationsOptions {
  enabled?: boolean;
  /**
   * Keep showing previous page data while a new query is loading.
   * Prevents the grid from blanking out when the user tweaks filters
   * (e.g. drags the confidence slider) — the old cards stay visible,
   * and we overlay a "refetching" indicator instead.
   */
  keepPreviousData?: boolean;
  /**
   * Window (ms) during which the cached result is considered fresh
   * and React Query won't refetch on remount. Cheap way to avoid
   * duplicate requests when the user toggles a filter back and forth.
   */
  staleTime?: number;
}

export const useGetReviewClassifications = (
  params: ReviewFilters,
  options: UseReviewClassificationsOptions = {}
) => {
  const { enabled = true, keepPreviousData = true, staleTime = 30_000 } = options;
  const limit = params.limit ?? DEFAULT_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.REVIEW_CLASSIFICATIONS, params],
    queryFn: ({ pageParam = 0 }) =>
      getReviewClassifications({ ...params, limit, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.metadata) return undefined;
      const { page, pageCount } = lastPage.metadata;
      if (page >= pageCount) return undefined;
      return page * lastPage.metadata.itemsPerPage;
    },
    // Hard guard — no request until the user picks a real scope.
    // This is what turns the "select a scope" onboarding state into
    // a true zero-request state on page load.
    enabled:
      enabled &&
      !!params.start_time &&
      !!params.end_time &&
      !!(params.location_id || params.machine_ids || params.device_mxids),
    placeholderData: keepPreviousData ? (prev) => prev : undefined,
    staleTime,
  });
};

export const useGetReviewDetail = (imageId: number, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REVIEW_DETAIL, imageId],
    queryFn: () => getReviewDetail(imageId),
    enabled: enabled && !!imageId,
  });
};

export const useReviewClassification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ imageId, payload }: { imageId: number; payload: SingleReviewPayload }) =>
      reviewClassification(imageId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_DETAIL, variables.imageId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_CLASSIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_ACTIVITY] });
    },
  });
};

export const useGetReviewActivity = (limit = 10) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REVIEW_ACTIVITY, limit],
    queryFn: () => getReviewActivity(limit),
    staleTime: 30_000,
  });
};

export const useBatchReviewClassifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BatchReviewPayload) => batchReviewClassifications(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_CLASSIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_DETAIL] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_ACTIVITY] });
    },
  });
};
